// /Users/donovan68/dva-frontend-vite/src/pages/freemium/DataStrategyOverview.tsx
// Freemium Step 3: Data Strategy Overview with embedded Data Product Playbook
// UPDATED: New layout - Page header above journey bar, removed redundant hero section
// FIXED: Removed hardcoded client_id '300' - now uses AuthContext session.client_id

import { useNavigate } from "react-router-dom";
import { FreemiumJourneyBarV8 } from "../../components/FreemiumJourneyBarV8";
import { MessagesContent } from "../../components/MessagesContent";
import { useFilter } from "../../contexts/FilterContext";
import { DataProductPlaybookLayout } from "../../components/playbook/DataProductPlaybookLayout";
import { Sparkles, RefreshCw, AlertCircle } from "lucide-react";
import { usePageContext } from "../../contexts/PageContext";
import { useEffect } from "react";
import FreemiumJourneyHeader from "../../components/freemium/FreemiumJourneyHeader";
import { useAuth } from "@/contexts/AuthContext";

export default function DataStrategyOverview() {
  const navigate = useNavigate();
  const { activeFilter } = useFilter();
  const { setPageContext } = usePageContext();

  // ✅ NEW: Get session from AuthContext
  const { session, sessionLoading, sessionError } = useAuth();

  // ✅ FIXED: Use session.client_id instead of hardcoded "300"
  const clientId = session?.client_id;

  // Set page context - empty breadcrumbs to hide global header tabs
  useEffect(() => {
    setPageContext({
      breadcrumbs: [], // Empty to hide tabs in global header (moved to page-level)
      tabs: ["Tools", "Documents", "Messages"]
    });
  }, [setPageContext]);

  // Determine if we should show messages
  const shouldShowMessages = activeFilter === 'Messages';

  // ✅ NEW: Show loading while session is being fetched
  if (sessionLoading) {
    return (
      <main className="max-w-[1440px] mx-auto px-6 pb-4">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="flex flex-col items-center gap-3">
            <RefreshCw className="w-8 h-8 text-[#18365E] animate-spin" />
            <p className="text-sm text-[#717182] font-medium">
              Loading session...
            </p>
          </div>
        </div>
      </main>
    );
  }

  // ✅ NEW: Handle case where no client_id is available
  if (!clientId) {
    return (
      <main className="max-w-[1440px] mx-auto px-6 pb-4">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="max-w-md p-6 bg-white rounded-xl shadow-lg border border-amber-200">
            <div className="flex items-center gap-3 mb-3">
              <AlertCircle className="w-6 h-6 text-amber-500" />
              <h3 className="text-lg font-bold text-[#18365E]">
                No Client Selected
              </h3>
            </div>
            <p className="text-sm text-[#717182] mb-4">
              {sessionError 
                ? `Session error: ${sessionError}` 
                : 'Please complete onboarding or select a client to view your data strategy overview.'
              }
            </p>
            <button
              onClick={() => navigate('/home')}
              className="px-4 py-2 bg-[#18365E] text-white rounded-lg hover:bg-[#18365E]/90 transition-all text-sm font-semibold"
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="max-w-[1440px] mx-auto px-6 pb-4">
      
      {/* Step Header with Tabs */}
      <FreemiumJourneyHeader
        stepNumber={3}
        stepName="Data Strategy Overview"
        description="Review your personalized data strategy summary and next steps."
      />

      {/* Journey Bar - Always Freemium */}
      <div className="mb-6">
        <FreemiumJourneyBarV8 
          currentStep={3} 
          isPersonalized={true}
          personalizationData={{
            strategy: 75,
            capabilities: 70,
            readiness: 65,
            docsCount: 3
          }}
          onPersonalizationClick={() => navigate("/personalize")}
          onStepClick={(stepId) => {
            if (stepId === 1) navigate("/home");
            else if (stepId === 2) navigate("/personalize");
            else if (stepId === 3) navigate("/freemium/strategy");
            else if (stepId === 4) navigate("/freemium/upgrade");
          }}
        />
      </div>

      {/* 3. Content */}
      
      {/* Conditional Content Based on Header Filter */}
      {shouldShowMessages ? (
        <MessagesContent />
      ) : (
        <>
          {/* Embedded Data Product Playbook */}
          <section className="rounded-2xl bg-white ring-1 ring-slate-200 shadow-sm overflow-hidden">
            <DataProductPlaybookLayout 
              clientId={clientId}
              showTitle={false}
            />
          </section>

          {/* Bottom CTA - Upgrade to Pro */}
          <section className="mt-6 rounded-2xl ring-1 ring-[#18365E]/20 bg-gradient-to-br from-white to-[#18365E]/5 p-8 shadow-md">
            <div className="flex items-start gap-6">
              <div className="w-16 h-16 rounded-2xl bg-[#18365E] flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-8 h-8 text-white" />
              </div>
                
              <div className="flex-1">
                <h4 className="text-xl font-bold text-[#18365E] mb-2">Ready to build your complete strategy?</h4>
                <p className="text-sm text-[#717182] font-medium mb-4">
                  Upgrade to DVA Pro for customized ROI modeling, implementation roadmaps, governance tracking, and executive-ready reports.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="rounded-xl bg-white ring-1 ring-[#e5e7eb] p-4">
                    <div className="text-2xl font-bold text-[#18365E] mb-1">Unlimited</div>
                    <div className="text-xs text-[#717182] font-medium">Custom Playbooks</div>
                  </div>
                  <div className="rounded-xl bg-white ring-1 ring-[#e5e7eb] p-4">
                    <div className="text-2xl font-bold text-[#18365E] mb-1">$8.6M</div>
                    <div className="text-xs text-[#717182] font-medium">Avg Customer ROI</div>
                  </div>
                  <div className="rounded-xl bg-white ring-1 ring-[#e5e7eb] p-4">
                    <div className="text-2xl font-bold text-[#18365E] mb-1">6mo</div>
                    <div className="text-xs text-[#717182] font-medium">Typical Payback</div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={() => navigate("/freemium/upgrade")}
                    className="inline-flex items-center justify-center px-6 py-3 rounded-xl font-semibold text-white bg-[#18365E] hover:bg-[#142b4b] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#18365E] transition-all shadow-sm hover:shadow-md"
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    Upgrade to DVA Pro
                  </button>
                    
                  <button
                    onClick={() => navigate("/home")}
                    className="inline-flex items-center justify-center px-6 py-3 rounded-xl font-semibold text-[#18365E] bg-white ring-1 ring-[#e5e7eb] hover:ring-[#18365E] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#18365E] transition-all"
                  >
                    Return to Home
                  </button>
                </div>
              </div>
            </div>
          </section>
        </>
      )}
    </main>
  );
}